<template>
    <div>
        <div class="card mb-4">
            <Link :href="route('admin.index')" class="text-blue-600 hover:underline text-sm">
                ← Kembali ke Admin Management
            </Link>
            <h2 class="text-xl font-bold mt-2">Role-Menu Assignment</h2>
            <p class="text-gray-500 text-sm">Pilih role dan assign menu yang dapat diakses</p>
        </div>
        
        <div class="card">
            <!-- Role Selector -->
            <div class="flex flex-wrap items-center gap-4 mb-4">
                <div class="flex items-center gap-2">
                    <label class="font-semibold">Pilih Role:</label>
                    <Dropdown 
                        v-model="selectedRole" 
                        :options="roles" 
                        optionLabel="name"
                        placeholder="-- Pilih Role --"
                        class="w-64"
                        @change="onRoleChange"
                    >
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex items-center gap-2">
                                <i :class="'pi ' + (slotProps.value.icon || 'pi-user')"></i>
                                <span>{{ slotProps.value.title || slotProps.value.name }}</span>
                            </div>
                            <span v-else>{{ slotProps.placeholder }}</span>
                        </template>
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <i :class="'pi ' + (slotProps.option.icon || 'pi-user')"></i>
                                <span>{{ slotProps.option.title || slotProps.option.name }}</span>
                            </div>
                        </template>
                    </Dropdown>
                </div>
                
                <div v-if="selectedRole" class="flex gap-2 ml-auto">
                    <Button label="Expand" icon="pi pi-angle-double-down" class="p-button-text p-button-sm" @click="expandAll" />
                    <Button label="Collapse" icon="pi pi-angle-double-up" class="p-button-text p-button-sm" @click="collapseAll" />
                </div>
            </div>
            
            <!-- Menu Tree -->
            <div v-if="selectedRole" class="border rounded-lg p-4">
                <div v-if="loadingMenus" class="flex justify-center items-center py-8">
                    <ProgressSpinner style="width: 40px; height: 40px" />
                </div>
                <Tree 
                    v-else
                    :value="menuTree" 
                    selectionMode="checkbox" 
                    v-model:selectionKeys="selectedMenus"
                    :expandedKeys="expandedKeys"
                    @node-expand="onNodeExpand"
                    @node-collapse="onNodeCollapse"
                    class="w-full border-0"
                >
                    <template #default="slotProps">
                        <div class="flex items-center gap-2">
                            <i :class="'pi ' + slotProps.node.icon" class="text-blue-500"></i>
                            <span>{{ slotProps.node.label }}</span>
                            <small v-if="slotProps.node.to" class="text-gray-400">({{ slotProps.node.to }})</small>
                        </div>
                    </template>
                </Tree>
            </div>
            
            <div v-else class="border rounded-lg p-8 text-center text-gray-400">
                <i class="pi pi-info-circle text-2xl mb-2"></i>
                <p>Pilih role dari dropdown di atas untuk melihat dan mengatur menu</p>
            </div>
            
            <!-- Save Button -->
            <div v-if="selectedRole" class="mt-4 flex justify-between items-center pt-4 border-t">
                <div class="text-sm text-gray-500">
                    {{ Object.keys(selectedMenus).filter(k => selectedMenus[k]?.checked).length }} menu dipilih
                </div>
                <Button 
                    label="Simpan Perubahan" 
                    icon="pi pi-save" 
                    class="p-button-primary" 
                    @click="saveRoleMenus"
                    :loading="saving"
                    :disabled="!hasChanges"
                />
            </div>
        </div>
        
        <Toast />
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import Tree from "primevue/tree";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import ProgressSpinner from "primevue/progressspinner";
import Toast from "primevue/toast";
import { Link } from '@inertiajs/inertia-vue3';

export default {
    name: "RoleMenusIndex",
    layout: AppLayout,
    components: {
        Tree,
        Button,
        Dropdown,
        ProgressSpinner,
        Toast,
        Link,
    },
    props: {
        roles: {
            type: Array,
            default: () => [],
        },
        menus: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedRole: null,
            roleMenus: [],
            selectedMenus: {},
            originalSelectedMenus: {},
            expandedKeys: {},
            loadingMenus: false,
            saving: false,
        };
    },
    computed: {
        menuTree() {
            return this.transformMenusToTree(this.roleMenus.length > 0 ? this.roleMenus : this.menus);
        },
        hasChanges() {
            return JSON.stringify(this.selectedMenus) !== JSON.stringify(this.originalSelectedMenus);
        },
    },
    methods: {
        transformMenusToTree(menus) {
            return menus.map(menu => ({
                key: menu.id.toString(),
                label: menu.text,
                icon: menu.icon || 'pi-circle',
                to: menu.to,
                data: menu,
                children: menu.children ? this.transformMenusToTree(menu.children) : undefined,
            }));
        },
        onRoleChange() {
            if (this.selectedRole) {
                this.loadRoleMenus();
            }
        },
        async loadRoleMenus() {
            this.loadingMenus = true;
            
            try {
                const response = await fetch(this.route('admin.rolemenus.get', this.selectedRole.id));
                const data = await response.json();
                
                this.roleMenus = data.menus;
                
                // Initialize selected menus from checked state
                this.selectedMenus = {};
                this.initializeSelectedMenus(data.menus);
                
                // Store original state for change detection
                this.originalSelectedMenus = JSON.parse(JSON.stringify(this.selectedMenus));
                
                // Expand all nodes
                this.expandAll();
            } catch (error) {
                console.error('Error loading role menus:', error);
            } finally {
                this.loadingMenus = false;
            }
        },
        initializeSelectedMenus(items) {
            items.forEach(item => {
                if (item.checked) {
                    this.selectedMenus[item.id.toString()] = { checked: true, partialChecked: false };
                }
                if (item.children) {
                    this.initializeSelectedMenus(item.children);
                }
            });
        },
        expandAll() {
            const expand = (items) => {
                items.forEach(item => {
                    this.expandedKeys[item.id.toString()] = true;
                    if (item.children) {
                        expand(item.children);
                    }
                });
            };
            expand(this.roleMenus.length > 0 ? this.roleMenus : this.menus);
            this.expandedKeys = { ...this.expandedKeys };
        },
        collapseAll() {
            this.expandedKeys = {};
        },
        onNodeExpand(node) {
            this.expandedKeys[node.key] = true;
            this.expandedKeys = { ...this.expandedKeys };
        },
        onNodeCollapse(node) {
            delete this.expandedKeys[node.key];
            this.expandedKeys = { ...this.expandedKeys };
        },
        getSelectedMenuIds() {
            return Object.keys(this.selectedMenus)
                .filter(key => this.selectedMenus[key]?.checked)
                .map(key => parseInt(key));
        },
        async saveRoleMenus() {
            this.saving = true;
            
            try {
                const response = await fetch(this.route('admin.rolemenus.sync', this.selectedRole.id), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        menu_ids: this.getSelectedMenuIds(),
                    }),
                });
                
                const data = await response.json();
                
                if (data.success) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Berhasil',
                        detail: data.message,
                        life: 3000,
                    });
                    
                    // Update original state
                    this.originalSelectedMenus = JSON.parse(JSON.stringify(this.selectedMenus));
                } else {
                    throw new Error(data.message);
                }
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message || 'Gagal menyimpan perubahan',
                    life: 3000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
}
</script>

<style scoped>
.card {
    background: white;
    padding: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.mb-4 {
    margin-bottom: 1rem;
}

.w-64 {
    width: 16rem;
}
</style>
